import React from 'react';
import { Sun, Sunset, Moon, Coffee, Zap, Bed } from 'lucide-react';

// Period → visual config. SLEEP_OVER is intentionally distinct from ACTIVE_NIGHT.
const PERIOD_CONFIG = {
  MORNING:      { label: 'Morning',      bg: 'bg-amber-100',  text: 'text-amber-800',  border: 'border-amber-300',  Icon: Coffee },
  AFTERNOON:    { label: 'Afternoon',    bg: 'bg-blue-100',   text: 'text-blue-800',   border: 'border-blue-300',   Icon: Sun },
  EVENING:      { label: 'Evening',      bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-300', Icon: Sunset },
  ACTIVE_NIGHT: { label: 'Active Night', bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300', Icon: Zap },
  SLEEP_OVER:   { label: 'Sleep-Over',   bg: 'bg-slate-200',  text: 'text-slate-700',  border: 'border-slate-400 border-dashed', Icon: Bed },
  CUSTOM:       { label: 'Custom',       bg: 'bg-gray-100',   text: 'text-gray-600',   border: 'border-gray-300',   Icon: Moon },
};

export const SHIFT_PERIODS = ['MORNING', 'AFTERNOON', 'EVENING', 'ACTIVE_NIGHT', 'SLEEP_OVER', 'CUSTOM'];

// Auto-classify by start/end time. Mirror of backend utils/shift_period.py.
// SLEEP_OVER is NEVER auto-assigned — manual selection only.
export function autoClassifyPeriod(startTime, endTime) {
  const toMinutes = (t) => {
    if (!t || !t.includes(':')) return null;
    const [h, m] = t.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
  };
  const s = toMinutes(startTime);
  const e = toMinutes(endTime);
  if (s === null || e === null) return 'CUSTOM';
  const crosses = e <= s;
  if (crosses && s >= 22 * 60 && e <= 6 * 60) return 'ACTIVE_NIGHT';
  if (crosses) return 'CUSTOM';
  if (s >= 6 * 60 && e <= 14 * 60) return 'MORNING';
  if (s >= 18 * 60 && e <= 23 * 60) return 'EVENING';
  if (s >= 14 * 60 && e <= 18 * 60) return 'AFTERNOON';
  return 'CUSTOM';
}

// Resolve period for a shift, preferring stored value and falling back to display-only auto.
export function getShiftPeriod(shift) {
  if (!shift) return { code: 'CUSTOM', auto: true };
  const stored = shift.shift_period;
  if (stored && PERIOD_CONFIG[stored]) return { code: stored, auto: false };
  const display = shift.shift_period_display || autoClassifyPeriod(shift.start_time, shift.end_time);
  return { code: PERIOD_CONFIG[display] ? display : 'CUSTOM', auto: !stored };
}

// Compact pill badge. `size` = 'sm' | 'md'. `showIcon` toggles glyph.
export function ShiftPeriodBadge({ period, auto = false, size = 'sm', showIcon = true, className = '' }) {
  const cfg = PERIOD_CONFIG[period] || PERIOD_CONFIG.CUSTOM;
  const Icon = cfg.Icon;
  const sizeCls = size === 'md'
    ? 'text-[11px] px-2 py-0.5 gap-1'
    : 'text-[9px] px-1.5 py-0.5 gap-0.5';
  return (
    <span
      className={`inline-flex items-center rounded-full border ${cfg.bg} ${cfg.text} ${cfg.border} font-medium leading-none ${sizeCls} ${className}`}
      title={`${cfg.label}${auto ? ' (auto)' : ''}`}
      data-testid={`shift-period-badge-${period}`}
    >
      {showIcon && <Icon className={size === 'md' ? 'w-3 h-3' : 'w-2.5 h-2.5'} />}
      <span>{cfg.label}{auto ? '*' : ''}</span>
    </span>
  );
}

export function ShiftPeriodBadgeForShift({ shift, ...rest }) {
  const { code, auto } = getShiftPeriod(shift);
  return <ShiftPeriodBadge period={code} auto={auto} {...rest} />;
}
